/**
 * Formats a number of dollars into thousands of dollars, rounded to the nearest tenth
 * @param num - The number of dollars to format
 * @returns The formatted value in thousands of dollars, or null if the input is invalid
 * @example
 * formatMoneyThousands(1500) // returns "1.5k"
 * formatMoneyThousands(10000) // returns "10k"
 * formatMoneyThousands(999) // returns "999"
 * formatMoneyThousands(null) // returns null
 * formatMoneyThousands(NaN) // returns null
 */
export const formatMoneyThousands = (num: number | null): string | null => {
  if (!num) return null;
  const value = Number(num);
  if (isNaN(value)) return null; //Ensure we have a valid number

  const inThousands = value / 1000;
  if (inThousands < 1) {
    // For values less than $1000, use the regular money formatter
    const exactFormatter = new Intl.NumberFormat("en-US", {
      currency: "USD",
      style: "currency",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return exactFormatter.format(value).replace("$", "");
  }

  // Check if the decimal part is 0
  const hasDecimal = inThousands % 1 !== 0;

  const formatter = new Intl.NumberFormat("en-US", {
    currency: "USD",
    style: "currency",
    minimumFractionDigits: hasDecimal ? 1 : 0,
    maximumFractionDigits: hasDecimal ? 1 : 0,
  });

  return `${formatter.format(inThousands).replace("$", "")}k`;
};

/**
 * Formats a number into a currency string, rounded to the nearest cent
 * @param num - The number to format (can be number, string, or null)
 * @param prefix - Optional prefix to prepend to the formatted value (defaults to "$")
 * @returns The formatted currency string with prefix, or null if the input is invalid
 * @example
 * formatMoney(1234.56) // returns "$1,234.56"
 * formatMoney(1000) // returns "$1,000"
 * formatMoney(62.8) // returns "$62.80"
 * formatMoney("1234.56") // returns "$1,234.56"
 * formatMoney(1234.56, "") // returns "1,234.56"
 * formatMoney(1000, "") // returns "1,000"
 * formatMoney(null) // returns null
 * formatMoney("invalid") // returns null
 */
export const formatMoney = (
  num: number | string | null,
  prefix: string = "$",
): string | null => {
  if (num === null || num === undefined) return null;
  if (num === 0) return `${prefix}0`;

  const value = Number(num);
  if (isNaN(value)) return null;

  const isWholeDollar = value % 1 === 0;

  const formatter = new Intl.NumberFormat("en-US", {
    currency: "USD",
    style: "currency",
    minimumFractionDigits: isWholeDollar ? 0 : 2,
    maximumFractionDigits: isWholeDollar ? 0 : 2,
  });

  const formatted = formatter.format(value);

  // Replace the default $ with the custom prefix
  return formatted.replace("$", prefix);
};

/**
 * Takes a number of cents and formats it as dollars, rounded to the nearest cent
 * @param num - The number of cents to format (can be number, string, or null)
 * @param prefix - Optional prefix to prepend to the formatted value (defaults to "$")
 * @returns The formatted currency string with prefix, or null if the input is invalid
 * @example
 * formatMoneyCents(1234) // returns "$12.34"
 * formatMoneyCents(1000) // returns "$10.00"
 * formatMoneyCents("1234") // returns "$12.34"
 * formatMoneyCents(1234, "") // returns "12.34"
 * formatMoneyCents(null) // returns null
 * formatMoneyCents("invalid") // returns null
 */
export const formatMoneyCents = (
  num: number | string | null,
  prefix: string = "$",
): string | null => {
  if (!num) return null;

  const value = Number(num);
  if (isNaN(value)) return null;

  return formatMoney(value / 100, prefix);
};

export const formatCentsOrDash = (value?: number | string | null): string => {
  if (value == null || (typeof value === "string" && value.trim() === ""))
    return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  // Always two decimals so cents never truncate to one place ($151.30, not
  // $151.3). Callers are exact invoice totals, where a ragged column reads as
  // a bug.
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n / 100);
};

export const formatCentsOrDashIfZero = (
  value?: number | string | null,
): string => {
  if (value == null || value === "" || Number(value) === 0) return "—";

  return formatCentsOrDash(value);
};

export const parseMaskedDollarInput = (value: string): number | null => {
  if (!value) return null;

  // Remove all non-numeric characters except decimal point and minus sign
  const cleanValue = value.replace(/[^0-9.-]+/g, "");
  const parsed = parseFloat(cleanValue);

  return isNaN(parsed) ? null : parsed;
};

/**
 * Parses a masked percent input string (e.g., "50%") into an integer
 * @param value - The masked percent input string
 * @returns The parsed percentage as an integer, or null if invalid
 * @example
 * parseMaskedPercentInput("50%") // returns 50
 * parseMaskedPercentInput("100%") // returns 100
 * parseMaskedPercentInput("50") // returns 50
 * parseMaskedPercentInput("") // returns null
 * parseMaskedPercentInput("abc") // returns null
 */
export const parseMaskedPercentInput = (value: string): number | null => {
  if (!value) return null;

  // Remove all non-numeric characters
  const cleanValue = value.replace(/[^0-9]+/g, "");
  const parsed = parseInt(cleanValue, 10);

  return isNaN(parsed) ? null : parsed;
};
